"use client";

import { useEffect, useRef } from "react";

interface AudioVisualizerProps {
  /** RMS level [0–1] from the active audio source. */
  rms: number;
  /** Whether the AI is currently speaking (controls ring color). */
  isAiSpeaking: boolean;
  /** Whether the user mic is active. */
  isListening: boolean;
  /** Size in pixels (default 160). */
  size?: number;
}

/**
 * Animated ring visualizer.
 *
 * - Outer ring pulses in scale with the RMS level.
 * - Color: blue when AI is speaking, green when user is listening, grey when idle.
 * - Inner circle shows a simple waveform using a Canvas element.
 */
export function AudioVisualizer({
  rms,
  isAiSpeaking,
  isListening,
  size = 160,
}: AudioVisualizerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const historyRef = useRef<number[]>(Array(60).fill(0));

  useEffect(() => {
    historyRef.current.push(rms);
    if (historyRef.current.length > 60) historyRef.current.shift();

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    const history = historyRef.current;
    const barWidth = w / history.length;

    const color = isAiSpeaking
      ? "rgba(99, 179, 237, 0.85)"
      : isListening
      ? "rgba(104, 211, 145, 0.85)"
      : "rgba(160, 174, 192, 0.5)";

    history.forEach((val, i) => {
      const barH = Math.max(2, val * h * 2.5);
      const x = i * barWidth;
      const y = (h - barH) / 2;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.roundRect(x + 1, y, Math.max(1, barWidth - 2), barH, 2);
      ctx.fill();
    });
  }, [rms, isAiSpeaking, isListening]);

  // Dynamic ring scale based on RMS
  const scale = 1 + rms * 0.35;
  const ringColor = isAiSpeaking
    ? "ring-blue-400"
    : isListening
    ? "ring-emerald-400"
    : "ring-slate-600";
  const glowColor = isAiSpeaking
    ? "shadow-blue-500/40"
    : isListening
    ? "shadow-emerald-500/40"
    : "";

  return (
    <div
      className="relative flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      {/* Outer pulse ring */}
      <div
        className={`absolute inset-0 rounded-full ring-4 ${ringColor} transition-all duration-100`}
        style={{
          transform: `scale(${scale})`,
          boxShadow:
            isAiSpeaking || isListening
              ? `0 0 ${Math.round(rms * 40 + 8)}px currentColor`
              : "none",
        }}
      />
      {/* Secondary ring (slower, always present when active) */}
      {(isAiSpeaking || isListening) && (
        <div
          className={`absolute inset-0 rounded-full ring-1 ${ringColor} opacity-40 animate-ping`}
        />
      )}
      {/* Inner canvas waveform */}
      <div
        className={`relative z-10 overflow-hidden rounded-full bg-slate-900 ring-2 ${ringColor} shadow-lg ${glowColor}`}
        style={{ width: size - 20, height: size - 20 }}
      >
        <canvas
          ref={canvasRef}
          width={size - 20}
          height={size - 20}
          className="block"
        />
      </div>
    </div>
  );
}
